/** @file Route definitions for the login and registration pages. */
import { Maybe } from '@codegrade/maybe';
import { featureFlagged } from '@/components/higher_order/FeatureFlagged';
import { lazyLoadView } from '@/components/higher_order/LazyLoader';
import { getDefault, NO_STUDENT_VIEW } from '../utils';
/**
 * Returns a meta object for a route.
 *
 * @param obj The meta object to merge with the default meta.
 * @returns The merged meta object.
 */
function getMeta(obj) {
    return {
        loginRequired: false,
        hideSidebar: true,
        hideFooter: true,
        getStudentViewPage: NO_STUDENT_VIEW,
        ...obj,
    };
}
const loaderOpts = Object.freeze({ class: 'hidden!' });
export default [
    {
        path: '/login/one-time-password',
        name: 'login-otp-verification',
        component: lazyLoadView({
            getComponent: () => import('@/pages/LoginV2/OTPAuthentication').then(getDefault),
            errorMessage: 'Could not load one-time password page',
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Login - One-time Password' }),
    },
    {
        path: '/login/select-tenant',
        name: 'login-tenant-list',
        component: lazyLoadView({
            getComponent: () => import('@/pages/LoginV2/TenantList').then(getDefault),
            errorMessage: 'Could not load select tenant page',
        }),
        meta: getMeta({ readableName: 'Login - Select Tenant' }),
    },
    {
        path: '/login/select-account/:session',
        name: 'login-select-account',
        component: lazyLoadView({
            getComponent: () => import('@/pages/LoginV2/SelectAccount').then(getDefault),
            errorMessage: 'Could not load select account page',
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Login - Select Account' }),
    },
    {
        path: '/login/:tenantId?',
        name: 'login',
        component: featureFlagged({
            featureFlag: 'NEW_LOGIN_AND_REGISTER',
            getComponent: () => import('@/pages/LoginV2/Login').then(getDefault),
            getTenantId: Maybe.empty,
            errorMessage: 'Could not load login page.',
            fallbackComponent: lazyLoadView({
                getComponent: () => import('@/pages/Login').then(getDefault),
                errorMessage: 'Could not load login page.',
            }),
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Login' }),
    },
    {
        path: '/forgot/:tenantId?',
        name: 'forgot',
        component: featureFlagged({
            featureFlag: 'NEW_LOGIN_AND_REGISTER',
            getComponent: () => import('@/pages/LoginV2/ForgotPassword').then(getDefault),
            getTenantId: Maybe.empty,
            errorMessage: 'Could not load forgot password page.',
            fallbackComponent: lazyLoadView({
                getComponent: () => import('@/pages/ForgotPassword').then(getDefault),
                errorMessage: 'Could not load forgot password page.',
            }),
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Forgot Password' }),
    },
    {
        path: '/tenants/:tenantId/users/:userId/reset_password',
        name: 'reset-password-new',
        component: featureFlagged({
            featureFlag: 'NEW_LOGIN_AND_REGISTER',
            getComponent: () => import('@/pages/LoginV2/ResetPassword').then(getDefault),
            getTenantId: Maybe.empty,
            errorMessage: 'Could not load reset password page.',
            fallbackComponent: lazyLoadView({
                errorMessage: 'Could not load reset password page.',
                getComponent: () => import('@/pages/ResetPassword').then(getDefault),
            }),
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Reset Password' }),
    },
    // This route is deprecated and redirects to `reset-password-new`
    {
        path: '/reset_password',
        name: 'reset-password',
        component: featureFlagged({
            featureFlag: 'NEW_LOGIN_AND_REGISTER',
            getComponent: () => import('@/pages/LoginV2/ResetPassword').then(getDefault),
            getTenantId: Maybe.empty,
            errorMessage: 'Could not load reset password page.',
            fallbackComponent: lazyLoadView({
                errorMessage: 'Could not load reset password page.',
                getComponent: () => import('@/pages/ResetPassword').then(getDefault),
            }),
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Reset Password' }),
    },
    {
        path: '/register',
        name: 'register-start',
        component: lazyLoadView({
            getComponent: () => import('@/pages/Register/Start').then(getDefault),
            errorMessage: 'Could not load register page',
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Register Start' }),
    },
    {
        path: '/register/account-details/:token',
        name: 'register-account-details',
        component: lazyLoadView({
            getComponent: () => import('@/pages/Register/AccountDetails').then(getDefault),
            errorMessage: 'Could not load register account details page',
            loaderOpts,
        }),
        meta: getMeta({ readableName: 'Register Account Details' }),
    },
    // A backup of the V1 login pages just in case V2 is broken.
    {
        path: '/v1/login/:tenantId?',
        name: 'v1-login',
        component: lazyLoadView({
            getComponent: () => import('@/pages/Login').then(getDefault),
            errorMessage: 'Could not load login page.',
        }),
        meta: getMeta({ readableName: 'Login', hideSidebar: false, hideFooter: false }),
    },
    {
        path: '/v1/forgot/:tenantId?',
        name: 'v1-forgot',
        component: lazyLoadView({
            getComponent: () => import('@/pages/ForgotPassword').then(getDefault),
            errorMessage: 'Could not load forgot password page.',
        }),
        meta: getMeta({ readableName: 'Forgot Password', hideSidebar: false, hideFooter: false }),
    },
    {
        path: '/v1/tenants/:tenantId/users/:userId/reset_password',
        name: 'v1-reset-password-new',
        component: lazyLoadView({
            errorMessage: 'Could not load reset password page.',
            getComponent: () => import('@/pages/ResetPassword').then(getDefault),
        }),
        meta: getMeta({ readableName: 'Reset Password', hideSidebar: false, hideFooter: false }),
    },
    // Pages where it just feels like an redirection.
    {
        path: '/login_and_redirect/:loginFile',
        name: 'login_and_redirect',
        component: lazyLoadView({
            getComponent: () => import('@/pages/LoginAndRedirect').then(getDefault),
            errorMessage: 'Could not load login and redirect page.',
        }),
        meta: getMeta({ readableName: 'Login and Redirect' }),
    },
    {
        path: '/sso_login/:blobId',
        name: 'sso_login',
        component: lazyLoadView({
            getComponent: () => import('@/pages/SsoLogin').then(getDefault),
            errorMessage: 'Could not load SSO login page.',
        }),
        meta: {
            loginRequired: false,
            hideSidebar: true,
            hideFooter: true,
            readableName: 'SSO Login',
            getStudentViewPage: NO_STUDENT_VIEW,
        },
    },
];
